import { t } from '../i18n';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { BunnyPlayer, type TimeState } from '../lib/playerjs';

/** Pleyer shuncha vaqtda javob bermasa, o'quvchiga chiqish yo'li beramiz. */
const READY_TIMEOUT_MS = 8000;

/**
 * Bunny Stream videosi.
 *
 * Tashqi ko'rinishi `<video>` bilan bir xil bo'lishi uchun ataylab
 * SHUNDAY tuzilgan: `onProgress` va `startAt` — `LessonViewPage` da
 * allaqachon mavjud mantiqqa to'g'ridan-to'g'ri ulanadi, ya'ni
 * "videoni ko'rish shart" va "qoldirgan joyidan davom etish"
 * funksiyalari o'zgarishsiz ishlaydi.
 */
export const BunnyVideo: FC<{
  src: string;
  /** Qaysi soniyadan davom ettirish. 0 bo'lsa boshidan. */
  startAt?: number;
  onProgress?: (status: { playedSeconds: number; played: number }) => void;
  onDuration?: (seconds: number) => void;
  onEnded?: () => void;
}> = ({ src, startAt = 0, onProgress, onDuration, onEnded }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  /*
   * Chaqiruvchilar har renderda yangi funksiya beradi. Ularni effekt
   * bog'liqligiga qo'ysak, pleyer har renderda qayta yaratilib video
   * boshidan ketardi — shuning uchun ref orqali eng oxirgisi
   * ishlatiladi. (Xuddi shu naqsh `LessonViewPage` da video joyini
   * saqlashda ham qo'llanilgan.)
   */
  const onProgressRef = useRef(onProgress);
  const onDurationRef = useRef(onDuration);
  const onEndedRef = useRef(onEnded);
  onProgressRef.current = onProgress;
  onDurationRef.current = onDuration;
  onEndedRef.current = onEnded;

  /** Davom ettirish BIR MARTA bajarilsin. */
  const resumed = useRef(false);

  /**
   * IFRAME MANZILI BARQAROR BO'LISHI SHART.
   *
   * Server har javobda YANGI imzo qo'yadi (`token` va `expires` 4 soatlik),
   * ya'ni `src` satri bir xil videoga ham har safar boshqacha bo'ladi.
   * React esa uni o'zgargan deb bilib atributni yangilaydi — iframe
   * QAYTA YUKLANADI va video boshidan ketadi yoki umuman ochilmaydi.
   *
   * Bu eski `/uploads/...` videolarida bo'lmagan: ular imzolanmaydi,
   * ya'ni manzil o'zgarmasdi. Muammo faqat Bunny'ga o'tgandan keyin
   * paydo bo'ldi.
   *
   * Shuning uchun videoni MANZIL emas, GUID aniqlaydi: token
   * yangilansa ham iframe tegilmaydi.
   */
  const guid = useMemo(() => {
    try {
      return new URL(src).pathname;
    } catch {
      return src;
    }
  }, [src]);

  const guidRef = useRef(guid);
  const srcRef = useRef(src);
  if (guidRef.current !== guid) {
    guidRef.current = guid;
    srcRef.current = src;
  }

  /** Pleyer javob bermadimi — o'quvchiga qora quti qoldirmaymiz. */
  const [noResponse, setNoResponse] = useState(false);

  useEffect(() => {
    const el = iframeRef.current;
    if (!el) return;

    resumed.current = false;
    setNoResponse(false);
    const player = new BunnyPlayer(el);

    /*
     * Pleyer `ready` yubormasa (iframe bloklangan, tarmoq yo'q,
     * WebView uni ko'rsatmayapti) o'quvchi shunchaki QORA QUTI ko'radi
     * va nima qilishni bilmaydi. Shu sababli kutamiz va keyin tashqi
     * havola taklif qilamiz.
     */
    const timeoutId = setTimeout(() => {
      if (!player.isReady) setNoResponse(true);
    }, READY_TIMEOUT_MS);

    player.on('ready', () => {
      clearTimeout(timeoutId);
      setNoResponse(false);
    });

    player.on('ready', () => {
      /*
       * Oxiriga yaqin joydan davom ettirmaymiz — video darhol tugab,
       * o'quvchi nima bo'lganini tushunmay qolardi. `<video>` yo'lida
       * ham xuddi shu qoida bor.
       */
      if (!resumed.current && startAt > 0) {
        resumed.current = true;
        player.setCurrentTime(startAt);
      }
    });

    player.on('timeupdate', (v: TimeState) => {
      const seconds = Number(v?.seconds);
      const duration = Number(v?.duration);
      if (!Number.isFinite(seconds)) return;

      if (Number.isFinite(duration) && duration > 0) {
        onDurationRef.current?.(duration);
      }

      onProgressRef.current?.({
        playedSeconds: seconds,
        played: duration > 0 ? seconds / duration : 0,
      });
    });

    player.on('ended', () => onEndedRef.current?.());

    return () => {
      clearTimeout(timeoutId);
      player.destroy();
    };
    // Videoning O'ZI (guid) o'zgarsagina pleyer qayta quriladi — imzo
    // yangilangani uchun emas.
    // `startAt` ataylab bog'liqlikda EMAS: u serverdan kechroq kelsa
    // video o'rtasida qayta sakrab ketardi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guid]);

  return (
    <>
      <iframe
        ref={iframeRef}
        src={srcRef.current}
        title={t('Dars videosi')}
        /*
         * `loading="lazy"` ATAYLAB YO'Q.
         *
         * Video — sahifaning asosiy mazmuni va ekranning yuqorisida
         * turadi, ya'ni kechiktirishdan yutuq yo'q. Zarari esa bor:
         * kechiktirilgan iframe ba'zi WebView'larda (Telegram ichidagi
         * brauzer) umuman yuklanmay qolishi mumkin va o'quvchi qora
         * quti ko'radi.
         */
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        style={{
          width: '100%',
          aspectRatio: '16 / 9',
          border: 0,
          borderRadius: 'var(--radius-md)',
          background: '#000',
          display: 'block',
        }}
      />

      {noResponse && (
        <div className="callout" style={{ marginTop: 'var(--space-2)' }}>
          <p className="text-sm">
            {t(
              'Video shu yerda ochilmadi. Telegram ichidagi brauzer uni koʻrsatmayotgan boʻlishi mumkin.',
            )}
          </p>
          <a
            className="btn btn-secondary btn-sm"
            style={{ marginTop: 'var(--space-2)' }}
            href={srcRef.current}
            target="_blank"
            rel="noreferrer"
          >
            {t('Videoni brauzerda ochish')}
          </a>
        </div>
      )}
    </>
  );
};
